import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, CircularProgress, Typography } from "@mui/material";
import { SLATE } from "../../constants/Colors";
import useApiCall from "../../hooks/useApiCall";

const AGENT_SERVICE_URL =
  process.env.REACT_APP_AGENT_SERVICE_URL || "http://localhost:8000";

export default function EmbeddedView() {
  const { agent_id } = useParams();
  const tokens = useSelector((state) => state?.tokens?.value);
  const { makeRequest } = useApiCall();

  const iframeRef = useRef(null);
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleIframeLoad = () => {
    try {
      const stored = localStorage.getItem('tokens');
      const userToken = stored ? JSON.parse(stored).user_token : null;
      if (userToken && iframeRef.current) {
        iframeRef.current.contentWindow.postMessage(
          { type: 'INSYNC_SSO', token: userToken },
          AGENT_SERVICE_URL
        );
      }
    } catch (_) {}
  };

  useEffect(() => {
    if (!agent_id) return;
    setLoading(true);
    makeRequest(`/v2/agent_instances/${agent_id}`, { method: "GET", authTokens: tokens })
      .then((data) => {
        setAgent(data?.agent || data);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load agent.");
        setLoading(false);
      });
  }, [agent_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const agentServiceName = agent?.metadata?.agent_service_name;
  const iframeSrc = agentServiceName
    ? `${AGENT_SERVICE_URL}/agents/${agentServiceName}/dashboard`
    : null;

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", gap: 2 }}>
        <CircularProgress size={20} />
        <Typography sx={{ color: SLATE[500], fontSize: "0.875rem" }}>Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <Typography sx={{ color: SLATE[500], fontSize: "0.875rem" }}>Failed to load agent.</Typography>
      </Box>
    );
  }

  if (!iframeSrc) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <Typography sx={{ color: SLATE[500], fontSize: "0.875rem" }}>
          No embedded app configured for this agent.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100vh", overflow: "hidden" }}>
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title={agent?.name || "Agent Dashboard"}
        style={{ width: "100%", height: "100%", border: "none" }}
        onLoad={handleIframeLoad}
      />
    </Box>
  );
}
